import React, { useEffect, useState, useCallback } from "react";
import { CheckCircle2, XCircle, CornerUpLeft, UserCheck, Clock, Inbox, FileText } from "lucide-react";
import api, { apiError } from "../lib/api";
import { PageHeader, Card, Badge, Loading, Modal, Spinner, EmptyState, fmtMoney, Avatar } from "../components/ui";

const STATUS_TONE = { PENDING: "warn", APPROVED: "ok", REJECTED: "bad" };

function ctxSummary(ctx = {}) {
  const out = [];
  if (ctx.days != null) out.push(`${ctx.days} day(s)`);
  if (ctx.amount != null) out.push(fmtMoney(ctx.amount));
  if (ctx.estimatedCost != null) out.push(`Est. ${fmtMoney(ctx.estimatedCost)}`);
  if (ctx.purpose) out.push(ctx.purpose);
  if (ctx.category) out.push(ctx.category);
  return out.join(" · ");
}

function Timeline({ inst }) {
  return (
    <div className="space-y-3" data-testid="flow-timeline">
      {inst.stages.map((s, i) => {
        const past = inst.history.find((h) => h.stage === i + 1);
        const isCurrent = inst.status === "PENDING" && inst.currentStage === i + 1;
        const tone = past ? (past.action === "REJECT" ? "text-red-500" : "text-emerald-600") : isCurrent ? "text-accent" : "text-slate-300";
        return (
          <div key={i} className="flex gap-3">
            <div className="flex flex-col items-center">
              <span className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${past ? "border-emerald-500 bg-emerald-500 text-white" : isCurrent ? "border-accent text-accent" : "border-slate-200 text-slate-300 dark:border-slate-700"}`}>{i + 1}</span>
              {i < inst.stages.length - 1 && <span className="my-0.5 h-6 w-px bg-slate-200 dark:bg-slate-700" />}
            </div>
            <div className="pb-2">
              <p className={`text-sm font-semibold ${tone}`}>{s.name}</p>
              <p className="text-xs text-slate-400">
                {s.approverType === "MANAGER" ? `Reporting Manager (L${s.level})` : s.approverType === "ROLE" ? s.role : s.approverType}
                {s.slaHours ? ` · SLA ${s.slaHours}h` : ""}
              </p>
              {past && <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{past.action} by {past.actorName}{past.comment ? ` — "${past.comment}"` : ""}</p>}
              {isCurrent && <p className="mt-0.5 text-xs font-medium text-accent">Awaiting: {inst.currentApproverLabel}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Approvals() {
  const [tab, setTab] = useState("inbox");
  const [inbox, setInbox] = useState(null);
  const [mine, setMine] = useState(null);
  const [active, setActive] = useState(null); // instance in modal
  const [comment, setComment] = useState("");
  const [delegateTo, setDelegateTo] = useState("");
  const [emps, setEmps] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/approvals/inbox").then(({ data }) => setInbox(data));
    api.get("/me/requests").then(({ data }) => setMine(data));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/employees?limit=200").then(({ data }) => setEmps(data.items || [])); }, []);

  const doAct = async (inst, action) => {
    if (action === "DELEGATE" && !delegateTo) return alert("Select an employee to delegate to.");
    setBusy(true);
    try {
      await api.post(`/approvals/${inst.id}/act`, { action, comment, delegateTo: delegateTo || undefined });
      setActive(null); setComment(""); setDelegateTo("");
      load();
    } catch (e) { alert(apiError(e.response?.data?.detail) || "Action failed"); }
    finally { setBusy(false); }
  };

  if (!inbox || !mine) return <Loading />;
  const list = tab === "inbox" ? inbox : mine;

  return (
    <div>
      <PageHeader title="Approvals" subtitle="Every leave, expense, travel & exit approval routes through the workflow engine" />

      <div className="mb-6 flex gap-1 border-b border-slate-200 dark:border-slate-800">
        <button data-testid="tab-inbox" onClick={() => setTab("inbox")} className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium ${tab === "inbox" ? "border-accent text-accent" : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"}`}>
          <Inbox size={15} /> Pending My Action {inbox.length > 0 && <span className="rounded-full bg-accent px-1.5 text-[10px] font-bold text-white" data-testid="inbox-count">{inbox.length}</span>}
        </button>
        <button data-testid="tab-myrequests" onClick={() => setTab("mine")} className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium ${tab === "mine" ? "border-accent text-accent" : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"}`}>
          <FileText size={15} /> My Requests
        </button>
      </div>

      {list.length === 0 ? (
        <EmptyState icon={tab === "inbox" ? Inbox : FileText} title={tab === "inbox" ? "Nothing awaiting your action" : "You haven't raised any requests yet"} hint={tab === "inbox" ? "Approvals assigned to you will appear here." : "Apply for leave, submit an expense or travel request to see it here."} />
      ) : (
        <div className="grid gap-3" data-testid="approvals-list">
          {list.map((i) => (
            <Card key={i.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`approval-${i.id}`}>
              <div className="flex items-start gap-3">
                <Avatar name={i.requesterName || "?"} size={38} />
                <div>
                  <p className="font-semibold text-slate-800 dark:text-slate-100">{i.flowName} <span className="font-mono text-xs text-slate-400">#{i.id.slice(-5)}</span></p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{i.requesterName} · {ctxSummary(i.context)}</p>
                  <div className="mt-1.5 flex items-center gap-2">
                    <Badge tone={STATUS_TONE[i.status]}>{i.status}</Badge>
                    <span className="text-xs text-slate-400"><Clock size={11} className="mr-0.5 inline" />Stage {i.currentStage}/{i.totalStages} · {i.status === "PENDING" ? i.currentApproverLabel : "Closed"}</span>
                  </div>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {tab === "inbox" && i.status === "PENDING" ? (
                  <>
                    <button data-testid={`quick-approve-${i.id}`} disabled={busy} onClick={() => { setComment(""); doAct(i, "APPROVE"); }} className="flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"><CheckCircle2 size={15} /> Approve</button>
                    <button data-testid={`open-${i.id}`} onClick={() => { setActive(i); setComment(""); setDelegateTo(""); }} className="btn-outline px-3 py-1.5 text-sm">Review</button>
                  </>
                ) : (
                  <button data-testid={`view-${i.id}`} onClick={() => { setActive(i); }} className="btn-outline px-3 py-1.5 text-sm">View timeline</button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal open={!!active} onClose={() => setActive(null)} title={active ? `${active.flowName} · ${active.requesterName}` : ""}
        footer={active && tab === "inbox" && active.status === "PENDING" && (
          <div className="flex w-full flex-wrap items-center justify-end gap-2">
            <button data-testid="act-sendback" disabled={busy} onClick={() => doAct(active, "SENDBACK")} className="flex items-center gap-1 rounded-md bg-amber-100 px-3 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-200 dark:bg-amber-500/15"><CornerUpLeft size={15} /> Send Back</button>
            <button data-testid="act-delegate" disabled={busy} onClick={() => doAct(active, "DELEGATE")} className="flex items-center gap-1 rounded-md bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"><UserCheck size={15} /> Delegate</button>
            <button data-testid="act-reject" disabled={busy} onClick={() => doAct(active, "REJECT")} className="flex items-center gap-1 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"><XCircle size={15} /> Reject</button>
            <button data-testid="act-approve" disabled={busy} onClick={() => doAct(active, "APPROVE")} className="flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">{busy ? <Spinner className="text-white" /> : <><CheckCircle2 size={15} /> Approve</>}</button>
          </div>
        )}>
        {active && (
          <div className="space-y-5">
            <div className="rounded-md bg-slate-50 px-4 py-3 text-sm dark:bg-slate-800/50">
              <span className="text-slate-500">Details: </span><span className="font-medium text-slate-800 dark:text-slate-100">{ctxSummary(active.context) || "—"}</span>
            </div>
            <Timeline inst={active} />
            {tab === "inbox" && active.status === "PENDING" && (
              <div className="space-y-2 border-t border-slate-100 pt-4 dark:border-slate-800">
                <textarea data-testid="approval-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment (optional, required for Send Back)…" className="input min-h-[70px]" />
                <div>
                  <label className="lbl mb-1 block">Delegate to (optional)</label>
                  <select data-testid="delegate-select" value={delegateTo} onChange={(e) => setDelegateTo(e.target.value)} className="input">
                    <option value="">— Select employee —</option>
                    {emps.map((e) => <option key={e.id} value={e.id}>{e.fullName} ({e.employeeCode})</option>)}
                  </select>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
